<!--
-->
<template>
  <div class="app" id="root">
    <context-view :visible="state.contextViewVisible" />
    <div v-if="state.globalLoadingVisible" class="app-global-loading">
      <spinner-ball-grid-pulse />
    </div>
    <!-- 'router-view' is a "global" component that is injected into the Vue component registry by vue-router. -->
    <router-view />
    <div v-if="state.languageIsChanging" class="alert alert-info">Language is changing...</div>
  </div>
</template>

<script setup lang="ts">
import { reactive, provide } from 'vue';
import ContextView from './ContextView.vue';
import spinnerBallGridPulse from './spinners/spinner-ball-grid-pulse.vue';
import { dictionaryServiceFactory } from './lib/dictionary-service-factory';
import { webStorage } from '@/utils/web-storage';
import { S_I18N } from '@/utils/web-storage-keys';
import { APP_CONST } from '@/utils/constants';
import { useI18n } from 'vue-i18n';

const i18n = useI18n();
const state = reactive({
  languageIsChanging: false,
  contextViewVisible: false,
  globalLoadingVisible: false,
});
const changeAppLanguage = async (language: string) => {
  if (!i18n) {
    // console.error('i18n instance is not initialized');
    return;
  }
  APP_CONST.lang = language;
  if (i18n.locale.value !== language) {
    // Changing languages is an async action, therefore the `languageIsChanging` property can be used
    // to show a loading/switching screen when language is being changed.
    state.languageIsChanging = true;
    try {
      const cache = webStorage.get(S_I18N) || {};
      if (!cache[language] || cache[language].length === 0) {
        // create an instance of the dictionary service
        const dictionaryServiceInstance = dictionaryServiceFactory.create();
        cache[language] = await dictionaryServiceInstance.fetchDictionaryData(language);
        webStorage.set(S_I18N, cache);
      }
      i18n.mergeLocaleMessage(language, cache[language]);
      i18n.locale.value = language;
    } catch (error) {
      console.error('Error changing language:', error);
    } finally {
      state.languageIsChanging = false;
    }
  }
};
const toggleContextView = () => {
  state.contextViewVisible = !state.contextViewVisible;
};
const showGlobalLoading = () => {
  state.globalLoadingVisible = true;
};
const hideGlobalLoading = () => {
  state.globalLoadingVisible = false;
};
provide('languageIsChanging', state.languageIsChanging);
provide('changeAppLanguage', changeAppLanguage);
provide('toggleContextView', toggleContextView);
provide('showGlobalLoading', showGlobalLoading);
provide('hideGlobalLoading', hideGlobalLoading);
</script>

<style lang="scss" scoped>
.app-global-loading {
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.65);
}
</style>
